import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCircle, KeyRound, Save, Loader2 } from "lucide-react";

const Profile = () => {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ full_name: user?.full_name || "", email: user?.email || "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", profile);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setSavingProfile(false);
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) return toast.error("New password must be ≥ 6 characters");
    if (pwd.new_password !== pwd.confirm) return toast.error("Passwords don't match");
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed. Next login will use the new one.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Change failed");
    }
    setSavingPwd(false);
  };

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-3xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Your account</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Profile</h1>
        <p className="text-sm text-brand-text-muted mt-1">Update your name, email and password.</p>
      </div>

      <Card className="p-6 rounded-2xl border-brand-border">
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted mb-4 flex items-center gap-2">
          <UserCircle className="w-3.5 h-3.5" /> Profile details
        </div>
        <form onSubmit={saveProfile} className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Full name</Label>
            <Input value={profile.full_name} data-testid="profile-name"
              onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Email</Label>
            <Input type="email" value={profile.email} data-testid="profile-email"
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" disabled={savingProfile} className="bg-brand-red hover:bg-brand-red-hover" data-testid="profile-save">
              {savingProfile ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save profile
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6 rounded-2xl border-brand-border">
        <div className="text-[10px] uppercase tracking-[0.2em] text-brand-text-muted mb-4 flex items-center gap-2">
          <KeyRound className="w-3.5 h-3.5" /> Change password
        </div>
        <form onSubmit={savePwd} className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Current password</Label>
            <Input type="password" required value={pwd.current_password} data-testid="pwd-current"
              onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-text-muted">New password</Label>
            <Input type="password" required value={pwd.new_password} data-testid="pwd-new"
              onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} placeholder="≥ 6 characters" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-text-muted">Confirm</Label>
            <Input type="password" required value={pwd.confirm} data-testid="pwd-confirm"
              onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" disabled={savingPwd} className="bg-brand-red hover:bg-brand-red-hover" data-testid="pwd-save">
              {savingPwd ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
              Change password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default Profile;
